import React, { useState, useEffect } from 'react';
import { Gift, Smartphone, Loader, Eye, EyeOff, Mail, CheckCircle } from 'lucide-react';

const API = 'https://qbdp-backend-production.up.railway.app/api';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login({ onLogin }) {
  const params = new URLSearchParams(window.location.search);
  const resetToken  = params.get('reset');
  const verifyToken = params.get('verify');
  const isMobile    = params.get('plan') === 'mobile';

  const [mode, setMode]         = useState(resetToken ? 'reset' : verifyToken ? 'verifying' : params.has('register') ? 'register' : 'login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom]           = useState('');
  const [societe, setSociete]   = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  // Auto-vérification si ?verify=TOKEN dans l'URL
  useEffect(() => {
    if (verifyToken) {
      setLoading(true);
      fetch(`${API}/auth/verify-email?token=${verifyToken}`)
        .then(r => r.json())
        .then(data => {
          if (data.token) {
            sessionStorage.setItem('qbdp_token', data.token);
            sessionStorage.setItem('qbdp_user', JSON.stringify(data.user));
            onLogin(data.user);
          } else {
            setError(data.error || 'Lien invalide ou expiré');
            setMode('login');
          }
        })
        .catch(() => { setError('Erreur de connexion'); setMode('login'); })
        .finally(() => setLoading(false));
    }
  }, []);

  const handleLogin = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (data.token) {
        sessionStorage.setItem('qbdp_token', data.token);
        sessionStorage.setItem('qbdp_user', JSON.stringify(data.user));
        onLogin(data.user);
      } else {
        setError('Identifiants incorrects');
      }
    } catch { setError('Erreur de connexion'); }
    setLoading(false);
  };

  const handleRegister = async () => {
    setError('');
    if (!nom)   { setError('Entrez votre nom'); return; }
    if (!email) { setError('Entrez votre email'); return; }
    if (!EMAIL_REGEX.test(email)) { setError('Adresse email invalide — vérifiez le format'); return; }
    if (password.length < 6) { setError('Mot de passe trop court (min. 6 caractères)'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom, email, password, societe })
      });
      const data = await res.json();
      if (data.pending) {
        setPendingEmail(data.email || email);
        setMode('pending');
      } else {
        setError(data.error || 'Erreur lors de la création du compte');
      }
    } catch { setError('Erreur de connexion'); }
    setLoading(false);
  };

  const handleForgot = async () => {
    setError(''); setSuccess('');
    if (!email) { setError('Entrez votre adresse email'); return; }
    if (!EMAIL_REGEX.test(email)) { setError('Adresse email invalide'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Un email de réinitialisation a été envoyé. Vérifiez votre boîte mail.');
      } else {
        setError(data.error || 'Erreur');
      }
    } catch { setError('Erreur de connexion'); }
    setLoading(false);
  };

  const handleReset = async () => {
    setError('');
    if (password.length < 6) { setError('Minimum 6 caractères'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-with-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: password })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Mot de passe modifié ! Vous pouvez vous connecter.');
        setTimeout(() => { window.location.href = '/'; }, 2000);
      } else {
        setError(data.error || 'Lien invalide ou expiré');
      }
    } catch { setError('Erreur de connexion'); }
    setLoading(false);
  };

  const inp = {
    width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
    padding: '12px 14px', fontSize: 14, outline: 'none', background: '#f9fafb',
    boxSizing: 'border-box',
  };

  const EyeBtn = () => (
    <button type="button" onClick={() => setShowPwd(v => !v)}
      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', lineHeight: 0, padding: 0 }}>
      {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  const titles = {
    login:     { title: 'Connexion',           sub: 'Accédez à votre dashboard' },
    register:  { title: 'Créer votre espace',  sub: isMobile ? "Accès à l'application mobile QBDP" : "15 jours d'essai gratuit — sans carte bancaire" },
    forgot:    { title: 'Mot de passe oublié', sub: 'Entrez votre email pour recevoir un lien de réinitialisation' },
    reset:     { title: 'Nouveau mot de passe',sub: 'Choisissez un nouveau mot de passe pour votre compte' },
    pending:   { title: 'Vérifiez votre email',sub: 'Un email de confirmation vous a été envoyé' },
    verifying: { title: 'Vérification...',     sub: 'Activation de votre compte en cours' },
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f0f5ff 0%, #f9fafb 100%)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400, background: 'white', borderRadius: 24, padding: '40px 40px', boxShadow: '0 24px 64px rgba(0,0,0,0.1)' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo1.png" style={{ width: 160, height: 160, marginBottom: 12, objectFit: 'contain', borderRadius: 24 }} alt="QBDP" />
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>
            {titles[mode]?.title}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {titles[mode]?.sub}
          </div>
        </div>

        {/* ── ÉCRAN VÉRIFICATION EN ATTENTE ── */}
        {mode === 'pending' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Mail size={32} color="#1a56db" />
              </div>
            </div>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 8 }}>
              Un email a été envoyé à
            </p>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 20 }}>
              {pendingEmail}
            </p>
            <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 24 }}>
              Cliquez sur le lien dans l'email pour activer votre compte et accéder au dashboard.
            </p>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#6b7280', textAlign: 'left', marginBottom: 20 }}>
              Vous ne trouvez pas l'email ? Vérifiez vos spams ou courriers indésirables.
            </div>
            <button onClick={() => { setMode('register'); setEmail(pendingEmail); setError(''); }}
              style={{ fontSize: 13, color: '#1a56db', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
              ← Modifier mon adresse email
            </button>
          </div>
        )}

        {/* ── VÉRIFICATION EN COURS ── */}
        {mode === 'verifying' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Loader size={40} color="#1a56db" style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: 16, color: '#6b7280', fontSize: 14 }}>Activation de votre compte...</p>
          </div>
        )}

        {/* FORMULAIRE LOGIN */}
        {mode === 'login' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email</div>
              <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="admin@votreentreprise.fr" />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Mot de passe</div>
              <div style={{ position: 'relative' }}>
                <input style={{ ...inp, paddingRight: 44 }} type={showPwd ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                <EyeBtn />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer', textDecoration: 'underline' }}>
                Mot de passe oublié ?
              </span>
            </div>
          </div>
        )}

        {/* FORMULAIRE INSCRIPTION */}
        {mode === 'register' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: isMobile ? '#eff6ff' : '#f0fdf4', border: `1px solid ${isMobile ? '#bfdbfe' : '#bbf7d0'}`, borderRadius: 10, padding: '10px 14px', fontSize: 12, color: isMobile ? '#1a56db' : '#15803d', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
              {isMobile ? <Smartphone size={14} /> : <Gift size={14} />}
              {isMobile ? "Créez votre compte pour accéder à l'app mobile QBDP" : "15 jours d'essai gratuit — accès complet, aucune carte requise"}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Votre nom *</div>
              <input style={inp} placeholder="Nom Prénom" value={nom} onChange={e => setNom(e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nom de l'établissement</div>
              <input style={inp} placeholder="Hôtel Bellevue, Clinique du Parc..." value={societe} onChange={e => setSociete(e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email *</div>
              <input style={inp} type="email" placeholder="vous@votreentreprise.fr" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Mot de passe *</div>
              <div style={{ position: 'relative' }}>
                <input style={{ ...inp, paddingRight: 44 }} type={showPwd ? 'text' : 'password'}
                  placeholder="Min. 6 caractères" value={password} onChange={e => setPassword(e.target.value)} />
                <EyeBtn />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Confirmer le mot de passe *</div>
              <input style={inp} type="password" placeholder="Répéter le mot de passe"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRegister()} />
            </div>
          </div>
        )}

        {/* FORMULAIRE MOT DE PASSE OUBLIÉ */}
        {mode === 'forgot' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email</div>
              <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="admin@votreentreprise.fr"
                onKeyDown={e => e.key === 'Enter' && handleForgot()} />
            </div>
          </div>
        )}

        {/* FORMULAIRE RESET MOT DE PASSE */}
        {mode === 'reset' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nouveau mot de passe</div>
              <div style={{ position: 'relative' }}>
                <input style={{ ...inp, paddingRight: 44 }} type={showPwd ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 6 caractères" />
                <EyeBtn />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Confirmer</div>
              <input style={inp} type="password" placeholder="Répéter le mot de passe"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleReset()} />
            </div>
          </div>
        )}

        {/* Erreur / Succès */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#991b1b', marginTop: 16 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#15803d', marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={14} /> {success}
          </div>
        )}

        {/* Bouton principal */}
        {!success && mode !== 'pending' && mode !== 'verifying' && (
          <button
            onClick={mode === 'login' ? handleLogin : mode === 'register' ? handleRegister : mode === 'forgot' ? handleForgot : handleReset}
            disabled={loading}
            style={{ width: '100%', background: '#1a56db', color: 'white', border: 'none', borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 20, opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {loading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Chargement...</> :
              mode === 'login'    ? 'Se connecter →' :
              mode === 'register' ? (isMobile ? 'Créer mon compte →' : "Démarrer l'essai gratuit →") :
              mode === 'forgot'   ? 'Envoyer le lien →' :
              'Enregistrer le mot de passe →'}
          </button>
        )}

        {/* Lien bas */}
        {mode !== 'pending' && mode !== 'verifying' && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#6b7280' }}>
            {mode === 'login' && (
              <>Pas encore de compte ? <span onClick={() => { setMode('register'); setError(''); setSuccess(''); }} style={{ color: '#1a56db', fontWeight: 600, cursor: 'pointer' }}>Essai gratuit 15 jours →</span></>
            )}
            {mode === 'register' && (
              <>Déjà un compte ? <span onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={{ color: '#1a56db', fontWeight: 600, cursor: 'pointer' }}>Se connecter</span></>
            )}
            {(mode === 'forgot' || mode === 'reset') && (
              <span onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={{ color: '#1a56db', fontWeight: 600, cursor: 'pointer' }}>← Retour à la connexion</span>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
